"use client"

import { useState, useEffect } from "react"
import { format } from "date-fns"
import { Search, Download, Filter, Trash, CheckCircle, XCircle, Menu, Calendar, X } from "lucide-react"

// Mock data for experts and invoices
const mockInvoices = [
  {
    expertId: "EXP001",
    expertName: "Dr. Jane Smith",
    initials: "DJS",
    email: "jane.smith@example.com",
    phone: "+1 (555) 123-4567",
    status: "Generated",
    generationDate: new Date(2023, 3, 15),
    signatureStatus: "Signed",
  },
  {
    expertId: "EXP002",
    expertName: "Prof. John Doe",
    initials: "PJD",
    email: "john.doe@example.com",
    phone: "+1 (555) 234-5678",
    status: "Generated",
    generationDate: new Date(2023, 3, 18),
    signatureStatus: "Pending",
  },
  {
    expertId: "EXP003",
    expertName: "Dr. Emily Johnson",
    initials: "DEJ",
    email: "emily.j@example.com",
    phone: "+1 (555) 345-6789",
    status: "Generated",
    generationDate: new Date(2023, 3, 20),
    signatureStatus: "Signed",
  },
  {
    expertId: "EXP004",
    expertName: "Dr. Michael Chen",
    initials: "DMC",
    email: "m.chen@example.com",
    phone: "+1 (555) 456-7890",
    status: "Generated",
    generationDate: new Date(2023, 3, 22),
    signatureStatus: "Signed",
  },
  {
    expertId: "EXP005",
    expertName: "Prof. Sarah Williams",
    initials: "PSW",
    email: "s.williams@example.com",
    phone: "+1 (555) 567-8901",
    status: "Generated",
    generationDate: new Date(2023, 3, 25),
    signatureStatus: "Pending",
  },
  {
    expertId: "EXP006",
    expertName: "Dr. Robert Taylor",
    initials: "DRT",
    email: "r.taylor@example.com",
    phone: "+1 (555) 678-9012",
    status: "Not Generated",
    generationDate: null,
    signatureStatus: "Pending",
  },
  {
    expertId: "EXP007",
    expertName: "Dr. Lisa Brown",
    initials: "DLB",
    email: "l.brown@example.com",
    phone: "+1 (555) 789-0123",
    status: "Generated",
    generationDate: new Date(2023, 4, 1),
    signatureStatus: "Signed",
  },
  {
    expertId: "EXP008",
    expertName: "Prof. David Miller",
    initials: "PDM",
    email: "d.miller@example.com",
    phone: "+1 (555) 890-1234",
    status: "Generated",
    generationDate: new Date(2023, 4, 3),
    signatureStatus: "Pending",
  },
  {
    expertId: "EXP009",
    expertName: "Dr. Amanda Wilson",
    initials: "DAW",
    email: "a.wilson@example.com",
    phone: "+1 (555) 901-2345",
    status: "Generated",
    generationDate: new Date(2023, 4, 5),
    signatureStatus: "Signed",
  },
]

// Mock data for orders
const mockOrders = [
  {
    orderId: "ORD-2023-001",
    subject: "Research Paper Review",
    orderStatus: "Completed",
    deadline: new Date(2023, 3, 20),
    amount: 250.0,
  },
  {
    orderId: "ORD-2023-002",
    subject: "Statistical Analysis",
    orderStatus: "In Progress",
    deadline: new Date(2023, 4, 5),
    amount: 350.0,
  },
  {
    orderId: "ORD-2023-003",
    subject: "Literature Review",
    orderStatus: "Completed",
    deadline: new Date(2023, 3, 15),
    amount: 200.0,
  },
  {
    orderId: "ORD-2023-004",
    subject: "Data Analysis Report",
    orderStatus: "Pending",
    deadline: new Date(2023, 4, 10),
    amount: 300.0,
  },
  {
    orderId: "ORD-2023-005",
    subject: "Research Methodology",
    orderStatus: "Completed",
    deadline: new Date(2023, 3, 25),
    amount: 275.0,
  },
]

export default function InvoiceGenerator() {
  // State for invoices
  const [invoices, setInvoices] = useState(mockInvoices)

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredInvoices, setFilteredInvoices] = useState(mockInvoices)
  const [showFilters, setShowFilters] = useState(false)

  // Filter states
  const [statusFilter, setStatusFilter] = useState("All Statuses")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [signatureFilter, setSignatureFilter] = useState("All")

  // State for selected invoices
  const [selectedInvoices, setSelectedInvoices] = useState([])
  const [selectAll, setSelectAll] = useState(false)

  // State for modal
  const [showModal, setShowModal] = useState(false)
  const [orders, setOrders] = useState([])

  // State for selected orders in the modal
  const [selectedOrders, setSelectedOrders] = useState([])
  const [selectAllOrders, setSelectAllOrders] = useState(false)

  // Check if any selected invoices are not generated
  const hasSelectedNotGenerated = selectedInvoices.some((id) => {
    const invoice = invoices.find((inv) => inv.expertId === id)
    return invoice && invoice.status === "Not Generated"
  })

  // Filter invoices based on search term and filters
  useEffect(() => {
    let filtered = [...invoices]

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (invoice) =>
          invoice.expertName.toLowerCase().includes(term) ||
          invoice.expertId.toLowerCase().includes(term) ||
          invoice.email.toLowerCase().includes(term),
      )
    }

    // Apply status filter
    if (statusFilter !== "All Statuses") {
      filtered = filtered.filter((invoice) => invoice.status === statusFilter)
    }

    // Apply date range filter
    if (startDate && endDate) {
      const start = new Date(startDate)
      const end = new Date(endDate)
      filtered = filtered.filter((invoice) => {
        if (!invoice.generationDate) return false
        const date = new Date(invoice.generationDate)
        return date >= start && date <= end
      })
    }

    // Apply signature filter
    if (signatureFilter !== "All") {
      filtered = filtered.filter((invoice) => invoice.signatureStatus === signatureFilter)
    }

    setFilteredInvoices(filtered)
  }, [invoices, searchTerm, statusFilter, startDate, endDate, signatureFilter])

  // Handle select all for invoices
  useEffect(() => {
    if (selectAll) {
      setSelectedInvoices(filteredInvoices.map((invoice) => invoice.expertId))
    } else if (selectedInvoices.length === filteredInvoices.length) {
      // If all were selected and selectAll is toggled off
      setSelectedInvoices([])
    }
  }, [selectAll, filteredInvoices])

  // Handle select all for orders
  useEffect(() => {
    if (selectAllOrders) {
      setSelectedOrders(orders.map((order) => order.orderId))
    } else if (selectedOrders.length === orders.length) {
      // If all were selected and selectAll is toggled off
      setSelectedOrders([])
    }
  }, [selectAllOrders, orders])

  // Handle individual invoice selection
  const toggleInvoiceSelection = (expertId) => {
    setSelectedInvoices((prev) =>
      prev.includes(expertId) ? prev.filter((id) => id !== expertId) : [...prev, expertId],
    )
  }

  // Handle individual order selection
  const toggleOrderSelection = (orderId) => {
    setSelectedOrders((prev) => (prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]))
  }

  // Handle select all toggle for invoices
  const toggleSelectAll = () => {
    setSelectAll(!selectAll)
  }

  // Handle select all toggle for orders
  const toggleSelectAllOrders = () => {
    setSelectAllOrders(!selectAllOrders)
  }

  // Check if all filtered invoices are selected
  useEffect(() => {
    if (filteredInvoices.length > 0 && selectedInvoices.length === filteredInvoices.length) {
      setSelectAll(true)
    } else {
      setSelectAll(false)
    }
  }, [selectedInvoices, filteredInvoices])

  // Check if all orders are selected
  useEffect(() => {
    if (orders.length > 0 && selectedOrders.length === orders.length) {
      setSelectAllOrders(true)
    } else {
      setSelectAllOrders(false)
    }
  }, [selectedOrders, orders])

  // Download PDF (mock function)
  const downloadPDF = (expertId) => {
    alert(`Downloading PDF for expert ${expertId}`)
    // In a real app, this would trigger the PDF download
  }

  // Delete selected invoices
  const deleteSelected = () => {
    if (selectedInvoices.length === 0) return

    setInvoices((prev) => prev.filter((invoice) => !selectedInvoices.includes(invoice.expertId)))
    setSelectedInvoices([])
  }

  // Generate invoices for selected experts
  const generateInvoices = () => {
    if (!hasSelectedNotGenerated) return

    // In a real app, this would generate invoices
    // For now, we'll just show the modal with mock orders
    setOrders(mockOrders)
    setSelectedOrders([]) // Reset selected orders
    setSelectAllOrders(false)
    setShowModal(true)

    // Update invoice status
    setInvoices((prev) =>
      prev.map((invoice) => {
        if (selectedInvoices.includes(invoice.expertId) && invoice.status === "Not Generated") {
          return {
            ...invoice,
            status: "Generated",
            generationDate: new Date(),
          }
        }
        return invoice
      }),
    )
  }

  // Close modal
  const closeModal = () => {
    setShowModal(false)
    setSelectedOrders([])
    setSelectAllOrders(false)
  }

  // Generate Invoice for selected orders
  const generateInvoiceForOrders = () => {
    if (selectedOrders.length === 0) {
      alert("Please select at least one order")
      return
    }

    alert(`Generating invoice for orders: ${selectedOrders.join(", ")}`)
    // In a real app, this would generate invoices for the selected orders
    closeModal()
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="mx-auto px-2 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button className="h-8 sm:h-9 w-8 sm:w-9 flex items-center justify-center rounded-md border border-gray-300 text-gray-700">
              <Menu className="h-4 sm:h-5 w-4 sm:w-5" />
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="h-8 sm:h-9 px-2 sm:px-4 rounded-md border border-gray-300 flex items-center text-xs sm:text-sm text-gray-700 hover:bg-gray-50"
            >
              <Filter className="h-3 sm:h-4 w-3 sm:w-4 mr-1 sm:mr-2" />
              Filter
            </button>
          </div>

          <h1 className="text-lg sm:text-xl font-semibold text-gray-800">Expert Invoices</h1>

          <div className="relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-28 sm:w-36 md:w-64 h-8 sm:h-9 pl-7 sm:pl-9 pr-2 sm:pr-4 rounded-md border border-gray-300 text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-[#001440] focus:border-[#001440]"
            />
            <Search className="h-3 sm:h-4 w-3 sm:w-4 text-gray-400 absolute left-2 sm:left-3 top-2.5" />
          </div>
        </div>
      </header>

      <main className="mx-auto px-4 py-4">
        {/* Filters */}
        {showFilters && (
          <div className="mb-4 sm:mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full rounded-md border border-gray-300 py-1.5 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-[#001440] focus:border-[#001440]"
                >
                  <option value="All Statuses">All Statuses</option>
                  <option value="Generated">Generated</option>
                  <option value="Not Generated">Not Generated</option>
                </select>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Date Range</label>
                <div className="flex space-x-2">
                  <div className="relative flex-1">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full rounded-md border border-gray-300 py-1.5 sm:py-2 pl-2 sm:pl-3 pr-8 sm:pr-10 text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-[#001440] focus:border-[#001440]"
                      placeholder="mm/dd/yyyy"
                    />
                    <Calendar className="h-3 sm:h-4 w-3 sm:w-4 text-gray-400 absolute right-2 sm:right-3 top-2 sm:top-2.5 pointer-events-none" />
                  </div>
                  <div className="relative flex-1">
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full rounded-md border border-gray-300 py-1.5 sm:py-2 pl-2 sm:pl-3 pr-8 sm:pr-10 text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-[#001440] focus:border-[#001440]"
                      placeholder="mm/dd/yyyy"
                    />
                    <Calendar className="h-3 sm:h-4 w-3 sm:w-4 text-gray-400 absolute right-2 sm:right-3 top-2 sm:top-2.5 pointer-events-none" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Signature</label>
                <select
                  value={signatureFilter}
                  onChange={(e) => setSignatureFilter(e.target.value)}
                  className="w-full rounded-md border border-gray-300 py-1.5 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-[#001440] focus:border-[#001440]"
                >
                  <option value="All">All</option>
                  <option value="Signed">Signed</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="hidden md:flex items-center space-x-3 mb-4">
          {selectedInvoices.length > 0 && (
            <button
              onClick={deleteSelected}
              className="h-9 px-4 rounded-md border border-red-200 bg-red-50 flex items-center text-sm text-red-600 hover:bg-red-100"
            >
              <Trash className="h-4 w-4 mr-2" />
              Delete ({selectedInvoices.length})
            </button>
          )}
          {hasSelectedNotGenerated && (
            <button
              onClick={generateInvoices}
              className="h-9 px-4 rounded-md bg-[#001440] flex items-center text-sm text-white hover:bg-[#00275a]"
            >
              Generate
            </button>
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block mb-8 overflow-x-auto">
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="w-10 px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-[#001440] focus:ring-[#001440]"
                    />
                  </th>
                  <th
                    scope="col"
                    className="w-12 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    #
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Expert
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Contact
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Date
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Signature
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvoices.map((invoice, index) => (
                  <tr
                    key={`${invoice.expertId}-${index}`}
                    className={`hover:bg-gray-50 transition-colors ${
                      selectedInvoices.includes(invoice.expertId) ? "bg-gray-50" : ""
                    }`}
                  >
                    <td className="w-10 px-4 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedInvoices.includes(invoice.expertId)}
                        onChange={() => toggleInvoiceSelection(invoice.expertId)}
                        className="h-4 w-4 rounded border-gray-300 text-[#001440] focus:ring-[#001440]"
                      />
                    </td>
                    <td className="w-12 px-4 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#001440] flex items-center justify-center">
                          <span className="text-white font-medium text-sm">{invoice.initials}</span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{invoice.expertName}</div>
                          <div className="text-sm text-gray-500">{invoice.expertId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{invoice.email}</div>
                      <div className="text-sm text-gray-500">{invoice.phone}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          invoice.status === "Generated"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.generationDate ? format(new Date(invoice.generationDate), "MMM dd, yyyy") : "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {invoice.signatureStatus === "Signed" ? (
                        <div className="flex items-center text-green-600">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          <span className="text-sm">Signed</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-amber-600">
                          <XCircle className="h-4 w-4 mr-1" />
                          <span className="text-sm">Pending</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      {invoice.status === "Generated" ? (
                        <button
                          onClick={() => downloadPDF(invoice.expertId)}
                          className="text-[#001440] hover:text-[#00275a] flex items-center"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </button>
                      ) : (
                        <span className="text-sm text-gray-400">No PDF</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Table View */}
        <div className="md:hidden mb-20">
          <div className="overflow-x-auto">
            <div className="border border-gray-200 rounded-md">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="w-10 px-3 py-3 text-left sticky left-0 bg-gray-50 z-10">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-gray-300 text-[#001440] focus:ring-[#001440]"
                      />
                    </th>
                    <th
                      scope="col"
                      className="w-10 px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      #
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]"
                    >
                      Expert
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]"
                    >
                      Contact
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]"
                    >
                      Date
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]"
                    >
                      Signature
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInvoices.map((invoice, index) => (
                    <tr
                      key={`mobile-${invoice.expertId}-${index}`}
                      className={`hover:bg-gray-50 transition-colors ${
                        selectedInvoices.includes(invoice.expertId) ? "bg-gray-50" : ""
                      }`}
                    >
                      <td className="w-10 px-3 py-4 whitespace-nowrap sticky left-0 bg-white z-10">
                        <input
                          type="checkbox"
                          checked={selectedInvoices.includes(invoice.expertId)}
                          onChange={() => toggleInvoiceSelection(invoice.expertId)}
                          className="h-4 w-4 rounded border-gray-300 text-[#001440] focus:ring-[#001440]"
                        />
                      </td>
                      <td className="w-10 px-2 py-4 text-sm text-gray-500 whitespace-nowrap">{index + 1}</td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-[#001440] flex items-center justify-center">
                            <span className="text-white font-medium text-xs">{invoice.initials}</span>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{invoice.expertName}</div>
                            <div className="text-xs text-gray-500">{invoice.expertId}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{invoice.email}</div>
                        <div className="text-xs text-gray-500">{invoice.phone}</div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            invoice.status === "Generated"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invoice.generationDate ? format(new Date(invoice.generationDate), "MMM dd, yyyy") : "-"}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        {invoice.signatureStatus === "Signed" ? (
                          <div className="flex items-center text-green-600">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            <span className="text-sm">Signed</span>
                          </div>
                        ) : (
                          <div className="flex items-center text-amber-600">
                            <XCircle className="h-4 w-4 mr-1" />
                            <span className="text-sm">Pending</span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                        {invoice.status === "Generated" ? (
                          <button
                            onClick={() => downloadPDF(invoice.expertId)}
                            className="text-[#001440] hover:text-[#00275a] flex items-center"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">No PDF</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Mobile Action Footer */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-2 flex justify-center z-20">
          {selectedInvoices.length > 0 ? (
            <div className="flex space-x-3">
              {selectedInvoices.length > 0 && (
                <button
                  onClick={deleteSelected}
                  className="px-3 py-2 rounded-md border border-red-200 bg-red-50 flex items-center text-sm text-red-600"
                >
                  <Trash className="h-4 w-4 mr-1" />
                  Delete ({selectedInvoices.length})
                </button>
              )}
              {hasSelectedNotGenerated && (
                <button
                  onClick={generateInvoices}
                  className="px-3 py-2 rounded-md bg-[#001440] flex items-center text-sm text-white"
                >
                  Generate
                </button>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-500">Select invoices to perform actions</div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">Order List</h2>
                <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6 overflow-auto max-h-[calc(90vh-120px)]">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="w-10 px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectAllOrders}
                            onChange={toggleSelectAllOrders}
                            className="h-4 w-4 rounded border-gray-300 text-[#001440] focus:ring-[#001440]"
                          />
                        </th>
                        <th
                          scope="col"
                          className="w-12 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Sr No
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Order ID
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Subject
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Order Status
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Deadline
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orders.map((order, index) => (
                        <tr
                          key={order.orderId}
                          className={`hover:bg-gray-50 ${selectedOrders.includes(order.orderId) ? "bg-gray-50" : ""}`}
                        >
                          <td className="w-10 px-4 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedOrders.includes(order.orderId)}
                              onChange={() => toggleOrderSelection(order.orderId)}
                              className="h-4 w-4 rounded border-gray-300 text-[#001440] focus:ring-[#001440]"
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {order.orderId}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{order.subject}</td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                order.orderStatus === "Completed"
                                  ? "bg-green-100 text-green-800"
                                  : order.orderStatus === "In Progress"
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {order.orderStatus}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {format(new Date(order.deadline), "MMM dd, yyyy")}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            ${order.amount.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-between">
                <div className="text-sm text-gray-500">
                  {selectedOrders.length > 0
                    ? `${selectedOrders.length} order${selectedOrders.length > 1 ? "s" : ""} selected`
                    : "Select orders to generate invoice"}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={generateInvoiceForOrders}
                    className={`px-4 py-2 rounded-md ${
                      selectedOrders.length > 0
                        ? "bg-[#001440] text-white hover:bg-[#00275a]"
                        : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
                    disabled={selectedOrders.length === 0}
                  >
                    Generate Invoice
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CSS to hide scrollbar */}
        <style jsx global>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          
          @media (max-width: 768px) {
            .overflow-x-auto {
              -webkit-overflow-scrolling: touch;
              scrollbar-width: none;
            }
            
            .overflow-x-auto::-webkit-scrollbar {
              display: none;
            }
          }
        `}</style>
      </main>
    </div>
  )
}
